/**
 * AI CONTEXT: mobile-first - Shopping cart context and persistence
 * Journal: J-20250809-01
 * 
 * Purpose:
 * - Manage cart items, quantities, and totals
 * - Persist cart in localStorage for session continuity
 * - Provide toast feedback for cart actions
 * 
 * Future considerations:
 * - Sync cart with backend for authenticated users
 * - Support coupons and promotions
 * - Add inventory checks and limits
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [cartClearedAfterPurchase, setCartClearedAfterPurchase] = useState(false);

  // AI CONTEXT: sales-pricing - Helper to get effective price (sales price if available, otherwise regular price)
  // Treat salesPrice of 0 as null/undefined (no discount) to avoid $0 checkout bug
  const getEffectivePrice = (product) => {
    if (product.salesPrice !== null && product.salesPrice !== undefined && product.salesPrice > 0) {
      return product.salesPrice;
    }
    return product.price;
  };

  useEffect(() => {
    // Load cart from localStorage
    const savedCart = localStorage.getItem('tolbert_cart');
    const clearedAfterPurchase = localStorage.getItem('tolbert_cart_cleared_after_purchase');
    
    console.log('Raw savedCart from localStorage:', savedCart);
    console.log('Cart cleared after purchase flag:', clearedAfterPurchase);
    
    // If cart was cleared after a successful purchase, don't restore it
    if (clearedAfterPurchase === 'true') {
      console.log('Cart was cleared after successful purchase - not restoring');
      setCartClearedAfterPurchase(true);
      return;
    }
    
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        console.log('Loading cart from localStorage:', parsedCart);
        console.log('Parsed cart length:', parsedCart.length);
        if (Array.isArray(parsedCart) && parsedCart.length > 0) {
          setCart(parsedCart);
          console.log('Cart restored from localStorage successfully');
        } else {
          console.log('localStorage cart is empty array, keeping current state');
        }
      } catch (error) {
        console.error('Error parsing cart from localStorage:', error);
        console.log('Keeping current cart state due to parsing error');
      }
    } else {
      console.log('No saved cart found in localStorage');
    }
  }, []);

  useEffect(() => {
    // Save cart to localStorage with additional safeguards
    console.log('Saving cart to localStorage:', cart);
    
    // Only save if cart is not empty and we're not in verification mode
    if (cart && cart.length > 0 && !isVerifying) {
      try {
        localStorage.setItem('tolbert_cart', JSON.stringify(cart));
        // Clear the "cleared after purchase" flag when new items are added
        localStorage.removeItem('tolbert_cart_cleared_after_purchase');
        setCartClearedAfterPurchase(false);
        console.log('Cart successfully saved to localStorage');
      } catch (error) {
        console.error('Error saving cart to localStorage:', error);
        // Try to save with a different key as backup
        try {
          localStorage.setItem('tolbert_cart_backup', JSON.stringify(cart));
          console.log('Cart saved to backup localStorage key');
        } catch (backupError) {
          console.error('Failed to save cart to backup key:', backupError);
        }
      }
    } else if (isVerifying) {
      console.log('Skipping cart save during verification mode');
    }
  }, [cart, isVerifying]);

  const addToCart = (product, quantity = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      
      if (existingItem) {
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        return [...prevCart, { ...product, quantity }];
      }
    });
    
    toast.success(`${product.name} added to cart`);
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
    toast.success('Item removed from cart');
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    console.log('Cart being cleared - Stack trace:', new Error().stack);
    if (isVerifying) {
      console.log('Preventing cart clear during verification');
      return;
    }
    setCart([]);
    // Set flag to prevent restoration after successful purchase
    localStorage.setItem('tolbert_cart_cleared_after_purchase', 'true');
    setCartClearedAfterPurchase(true);
    toast.success('Cart cleared');
  };

  const clearCartAfterPurchase = () => {
    console.log('Cart being cleared after successful purchase');
    setCart([]);
    // Set flag to prevent restoration after successful purchase
    localStorage.setItem('tolbert_cart_cleared_after_purchase', 'true');
    setCartClearedAfterPurchase(true);
    // Don't show toast for purchase-related clearing
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (getEffectivePrice(item) * item.quantity), 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const getTotalEntries = () => {
    return cart.reduce((total, item) => total + (item.entries * item.quantity), 0);
  };

  const checkout = async (paymentMethod, user) => {
    setLoading(true);
    
    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Create purchase records
      const purchases = cart.map(item => {
        const effectivePrice = getEffectivePrice(item);
        const creditsPerItem = Number(item.credits) > 0 ? Number(item.credits) : 1;
        return {
          id: Date.now() + Math.random(),
          productId: item.id,
          productName: item.name,
          quantity: item.quantity,
          price: effectivePrice,  // AI CONTEXT: sales-pricing - Use effective price for checkout
          originalPrice: item.price,  // Keep original price for reference
          salesPrice: item.salesPrice,  // Keep sales price for reference
          total: effectivePrice * item.quantity,
          entries: item.entries * item.quantity,
          credits: creditsPerItem,
          tier: item.tier,
          date: new Date().toISOString().split('T')[0],
          paymentMethod
        };
      });
      
      // Clear cart after successful checkout
      clearCartAfterPurchase();
      // Fire global event for post-purchase effects (e.g., confetti)
      try {
        window.dispatchEvent(new Event('purchase-success'));
      } catch (_e) {}
      
      return {
        success: true,
        purchases,
        total: getCartTotal(),
        entries: getTotalEntries()
      };
    } catch (error) {
      toast.error('Payment failed. Please try again.');
      return {
        success: false,
        error: error.message
      };
    } finally {
      setLoading(false);
    }
  };

  const validateCartData = (cartData) => {
    if (!Array.isArray(cartData)) {
      console.log('Cart data is not an array:', cartData);
      return false;
    }
    
    if (cartData.length === 0) {
      console.log('Cart data is empty array');
      return false;
    }
    
    // Validate each cart item has required properties
    for (let i = 0; i < cartData.length; i++) {
      const item = cartData[i];
      if (!item || typeof item !== 'object') {
        console.log(`Cart item ${i} is not a valid object:`, item);
        return false;
      }
      
      if (!item.id || !item.name || typeof item.price !== 'number' || typeof item.quantity !== 'number') {
        console.log(`Cart item ${i} missing required properties:`, item);
        return false;
      }
    }
    
    return true;
  };

  // AI CONTEXT: sales-pricing - Sync cart items with current product data
  // Updates prices and salesPrices from current product data while preserving cart-specific data
  const syncCartWithProducts = (products) => {
    if (!products || !Array.isArray(products) || products.length === 0) {
      return;
    }
    
    setCart(prevCart => {
      if (!prevCart || prevCart.length === 0) {
        return prevCart;
      }
      
      const updatedCart = prevCart.map(cartItem => {
        const currentProduct = products.find(p => String(p.id) === String(cartItem.id));
        
        if (currentProduct) {
          // Update price and salesPrice from current product data
          // Preserve quantity and other cart-specific properties
          // Always use current product's salesPrice (even if null/undefined, meaning sale ended)
          return {
            ...cartItem,
            price: currentProduct.price,
            salesPrice: currentProduct.salesPrice, // Use current product's salesPrice (null/undefined if no sale)
            // Also update other product properties that might have changed
            name: currentProduct.name || cartItem.name,
            entries: currentProduct.entries || currentProduct.credits || cartItem.entries,
            credits: currentProduct.credits ?? currentProduct.entries ?? cartItem.credits ?? 1,
            tier: currentProduct.tier || cartItem.tier,
            shippingCost: currentProduct.shippingCost !== undefined ? currentProduct.shippingCost : 5,
          };
        }
        
        // If product not found, keep cart item as-is
        return cartItem;
      });
      
      // Only update if there were actual changes
      const hasChanges = updatedCart.some((item, index) => {
        const original = prevCart[index];
        return item.price !== original.price ||
               item.salesPrice !== original.salesPrice ||
               item.name !== original.name ||
               item.credits !== original.credits;
      });
      
      if (hasChanges) {
        console.log('Cart synced with current product data');
        return updatedCart;
      }
      
      return prevCart;
    });
  };

  const restoreCartFromStorage = () => {
    // Check if cart was cleared after a successful purchase
    const clearedAfterPurchase = localStorage.getItem('tolbert_cart_cleared_after_purchase');
    if (clearedAfterPurchase === 'true') {
      console.log('Cart was cleared after successful purchase - not restoring');
      return false;
    }
    
    // Try primary localStorage key first
    let savedCart = localStorage.getItem('tolbert_cart');
    let source = 'primary';
    
    // If primary key is empty or invalid, try backup key
    if (!savedCart || savedCart === '[]' || savedCart === 'null') {
      savedCart = localStorage.getItem('tolbert_cart_backup');
      source = 'backup';
      console.log('Primary cart key empty, trying backup key');
    }
    
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        
        // Validate cart data before using it
        if (validateCartData(parsedCart)) {
          setCart(parsedCart);
          console.log(`Cart manually restored from ${source} localStorage:`, parsedCart);
          
          // If we used backup key, also restore to primary key
          if (source === 'backup') {
            localStorage.setItem('tolbert_cart', savedCart);
            console.log('Restored cart to primary localStorage key');
          }
          
          return true;
        } else {
          console.log(`Cart from ${source} localStorage failed validation:`, parsedCart);
        }
      } catch (error) {
        console.error(`Error restoring cart from ${source} localStorage:`, error);
      }
    } else {
      console.log('No cart found in localStorage (primary or backup)');
    }
    return false;
  };

  const value = {
    cart,
    loading,
    isVerifying,
    cartClearedAfterPurchase,
    setIsVerifying,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    clearCartAfterPurchase,
    getCartTotal,
    getCartCount,
    getTotalEntries,
    checkout,
    restoreCartFromStorage,
    syncCartWithProducts
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}; 